/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import {
  Action,
  ActionDispatcher,
  compoundLogicalAction,
  ConstantAction,
  constantAction,
  exitAction,
  FlowStateWithError,
  LogicalAction,
  logicalAction,
} from '@gh/common-shared';
import { Message, MessageBasket, MessageSeverity } from '@gh/core-messages';
import { TranslationParams } from '@gh/core-mls';
import {
  Confirmation,
  ConfirmationDialogConfig,
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesNoConfirmation,
} from '@gh/core-ui';
import { wrapInObservable } from '@gh/core-util';
import { catchError$, map$ } from '@gh/rx/operators';
import { assign, isFunction, isNil, omit } from 'lodash';
import { Observable } from 'rxjs';

export interface CommonFlowState<T> {
  value: T;
  error?: any;
}

export interface ConfirmationDialogActions {
  yes?: string;
  no?: string;
  cancel?: string;
}

export function mapValueAction<FS extends CommonFlowState<T>, T>(transform: TransformFn<any, any>): ConstantAction<FS> {
  return constantAction(
    'done',
    (dispatch, flowState: FS) =>
      dispatch('done', assign({}, flowState, {
        value: transform(flowState.value),
      })));
}

@Injectable()
export class CommonActions {
  constructor(private dialogService: DialogService,
              private waitingService: WaitingService,
              private notificationService: NotificationService) {
  }

  textMessage<FS>(config: TextMessageDialogConfig<string> | TransformFn<FS, TextMessageDialogConfig<string>>,
                  actions: Hash<string>,
                  matConfig?: MatDialogConfig): Action<FS> {
    return (dispatcher: ActionDispatcher, state: FS) =>
      this.dialogService.openWithInput(textMessageDialogRoute, isFunction(config) ? config(state) : config, matConfig)
        .afterClosed()
        .subscribe((button) => dispatcher(actions[button] || 'otherwise', state));
  }

  notification<FS>(message: Message): ConstantAction<FS> {
    return constantAction(
      'next',
      (dispatch: ActionDispatcher, flowState: FS) => {
        this.notificationService.message(message);
        dispatch('next', flowState);
      },
    );
  }

  confirmation<FS>(config: ConfirmationDialogConfig, actions: ConfirmationDialogActions): Action<FS> {
    return (dispatch: ActionDispatcher, flowState: FS) => {
      this.dialogService.confirmation(config).subscribe((answer) => {
        switch (answer) {
          case Confirmation.Yes:
            if (actions.yes) {
              dispatch(actions.yes, flowState);
            }
            break;
          case Confirmation.No:
            if (actions.no) {
              dispatch(actions.no, flowState);
            }
            break;
          default:
            if (actions.cancel) {
              dispatch(actions.cancel, flowState);
            }
            break;
        }
      });
    };
  }

  warningNotification<FS>(key: string, params?: TranslationParams): ConstantAction<FS> {
    return this.notification({ severity: MessageSeverity.Warning, key, params: params || {} });
  }

  errorNotification<FS>(key: string, params?: TranslationParams): ConstantAction<FS> {
    return this.notification({ severity: MessageSeverity.Error, key, params: params || {} });
  }

  yesNoConfirmation<FS>(label: string, translationParams?: TranslationParams): LogicalAction<FS> {
    return compoundLogicalAction('confirmation.success', 'confirmation.fail', {
      'start': this.confirmation(yesNoConfirmation(label, translationParams), {
        yes: 'success',
        no: 'fail',
      }),
      'success': exitAction('confirmation.success'),
      '*': exitAction('confirmation.fail'),
    });
  }

  validate<FS>(validator: (state: FS) => Maybe<MessageBasket> | Observable<any>): LogicalAction<FS> {
    const success = 'validate.success';
    const fail = 'validate.fail';

    return logicalAction(
      success,
      fail,
      (dispatch: ActionDispatcher, state: FS) => {
        wrapInObservable(validator(state)).pipe(
          catchError$((error) => [error]))
          .subscribe((error) => {
            if (isNil(error)) {
              dispatch(success, state);
            } else {
              dispatch(fail, assign({}, state, { error }));
            }
          });
      });
  }

  validateWithEffect<FS>(predicate: PredicateFn<FS>, sideEffect: ProcedureFn<FS>): LogicalAction<FS> {
    const success = 'validate.success';
    const fail = 'validate.fail';

    return logicalAction(
      success,
      fail,
      (dispatch: ActionDispatcher, state: FS) => {
        if (predicate(state)) {
          sideEffect(state);

          dispatch(fail, state);
        } else {
          dispatch(success, state);
        }
      });
  }

  execute<FS extends CommonFlowState<T>, T>(executor: (state: FS) =>
    Observable<T>, skipWaiting?: boolean, errorHandler?: Function): LogicalAction<FS> {
    const success = 'execute.success';
    const fail = 'execute.fail';

    return logicalAction(
      success,
      fail,
      (dispatch: ActionDispatcher, flowState: FS) =>
        executor(flowState).pipe(skipWaiting ? map$((res: any) => res) : this.waitingService.operator()).subscribe(
          (response) => dispatch(success, assign({}, flowState, { value: response })),
          (error) => {
            if (errorHandler) {
              errorHandler(error);
            }
            return dispatch(fail, assign({}, flowState, { error }));
          },
        ));
  }

  async<FS>(executor: (state: FS) => Observable<FS>): LogicalAction<FS> {
    const success = 'execute.success';
    const fail = 'execute.fail';

    return logicalAction(
      success,
      fail,
      (dispatch: ActionDispatcher, flowState: FS) =>
        executor(flowState).pipe(this.waitingService.operator()).subscribe(
          (response) => dispatch(success, response),
          (error) => dispatch(fail, assign({}, flowState, { error })),
        ));
  }

  notifications<BaseFS, FS extends FlowStateWithError<BaseFS>>(): ConstantAction<FS> {
    const actionName = 'notifications.end';

    return constantAction(actionName, (dispatch, flowState: FS) => {
      const { error } = flowState;

      if (!isNil(error)) {
        this.notificationService.processAll(error);
      }

      dispatch(actionName, omit(flowState, 'error'));
    });
  }
}
